import { track } from '@turfchale/analytics'
import { useAuthStore } from '@turfchale/supabase'
import { Download, Menu, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'

import { Logo } from '@/components/ui/Logo'

const links = [
  { href: '/turfs', label: 'Browse Turfs' },
  { href: '#cities', label: 'Cities' },
  { href: 'https://portal.turfchale.com', label: 'For Owners' },
  { href: '#faq', label: 'FAQ' },
]

// Cross-origin links (e.g. the portal) must break out of the installed PWA's
// standalone window into the browser, which then hands off to the portal app
// if it's installed. Same-origin/hash links stay in-app.
const isExternal = (href: string) => href.startsWith('http')
const externalProps = (href: string) =>
  isExternal(href) ? { rel: 'noopener noreferrer', target: '_blank' } : {}

export function Navbar() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const reduced = useReducedMotion()
  const isAuthed = useAuthStore((s) => Boolean(s.session))

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const close = () => setOpen(false)

  return (
    <>
      <nav
        className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 h-17 transition-all duration-300"
        style={{
          backdropFilter: 'blur(16px)',
          background: scrolled ? 'rgba(11,28,16,0.95)' : 'rgba(11,28,16,0.8)',
          borderBottom: '1px solid var(--lime-line)',
        }}
      >
        <Logo external fontSize="1.4rem" />

        {/* Desktop links */}
        <ul className="hidden md:flex items-center gap-8 list-none">
          {links.map((l) => (
            <li key={l.href}>
              <a
                className="text-sm transition-colors duration-200"
                href={l.href}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted)')}
                style={{ color: 'var(--muted)' }}
                {...externalProps(l.href)}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Desktop right-side */}
        <div className="hidden md:flex items-center gap-3">
          <a
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-all duration-200 hover:opacity-80"
            href="/install"
            onClick={() => track('landing_cta_clicked', { cta: 'install' })}
            style={{ border: '1px solid var(--lime-line)', color: 'var(--text)' }}
          >
            <Download size={15} />
            Install
          </a>
          <a
            className="text-sm font-semibold px-4 py-2 rounded-full transition-all duration-200 hover:opacity-80"
            href={isAuthed ? '/app' : '/auth'}
            onClick={() => track('landing_cta_clicked', { cta: 'nav' })}
            style={{
              border: '1px solid var(--lime-line)',
              color: 'var(--text)',
            }}
          >
            {isAuthed ? 'Dashboard' : 'Sign In'}
          </a>
          {!isAuthed && (
            <a
              className="inline-flex items-center gap-1 text-sm font-bold px-5 py-2 rounded-full transition-all duration-200 hover:opacity-85 hover:scale-[1.03]"
              href="https://portal.turfchale.com"
              rel="noopener noreferrer"
              style={{ background: 'var(--lime)', color: 'var(--bg)' }}
              target="_blank"
            >
              List Your Turf →
            </a>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          aria-label="Toggle menu"
          className="md:hidden p-1"
          onClick={() => setOpen((o) => !o)}
          style={{ color: 'var(--text)' }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            animate={reduced ? {} : { opacity: 1, y: 0 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-8"
            exit={reduced ? {} : { opacity: 0, y: -12 }}
            initial={reduced ? false : { opacity: 0, y: -12 }}
            style={{ backdropFilter: 'blur(20px)', background: 'rgba(11,28,16,0.97)', top: 68 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            {links.map((l, i) => (
              <motion.a
                animate={reduced ? {} : { opacity: 1, y: 0 }}
                className="text-2xl font-semibold transition-colors"
                href={l.href}
                initial={reduced ? false : { opacity: 0, y: 10 }}
                key={l.href}
                onClick={close}
                style={{ color: 'var(--muted)' }}
                transition={{ delay: 0.06 + i * 0.05, duration: 0.22 }}
                {...externalProps(l.href)}
              >
                {l.label}
              </motion.a>
            ))}
            <motion.a
              animate={reduced ? {} : { opacity: 1, y: 0 }}
              className="inline-flex items-center gap-1.5 text-base font-semibold px-6 py-2 rounded-full mt-2"
              href="/install"
              initial={reduced ? false : { opacity: 0, y: 10 }}
              onClick={() => {
                track('landing_cta_clicked', { cta: 'install' })
                close()
              }}
              style={{ border: '1px solid var(--lime-line)', color: 'var(--text)' }}
              transition={{ delay: 0.06 + (links.length + 0) * 0.05, duration: 0.22 }}
            >
              <Download size={16} />
              Install App
            </motion.a>
            <motion.a
              animate={reduced ? {} : { opacity: 1, y: 0 }}
              className="text-base font-semibold px-6 py-2 rounded-full"
              href={isAuthed ? '/app' : '/auth'}
              initial={reduced ? false : { opacity: 0, y: 10 }}
              onClick={() => {
                track('landing_cta_clicked', { cta: 'nav' })
                close()
              }}
              style={{ border: '1px solid var(--lime-line)', color: 'var(--text)' }}
              transition={{ delay: 0.06 + (links.length + 1) * 0.05, duration: 0.22 }}
            >
              {isAuthed ? 'Dashboard' : 'Sign In'}
            </motion.a>
            {!isAuthed && (
              <motion.a
                animate={reduced ? {} : { opacity: 1, y: 0 }}
                className="text-lg font-bold px-8 py-3 rounded-full mt-1"
                href="https://portal.turfchale.com"
                initial={reduced ? false : { opacity: 0, y: 10 }}
                onClick={close}
                rel="noopener noreferrer"
                style={{ background: 'var(--lime)', color: 'var(--bg)' }}
                target="_blank"
                transition={{ delay: 0.06 + (links.length + 2) * 0.05, duration: 0.22 }}
              >
                List Your Turf →
              </motion.a>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
